/**
 * Utilitaire pour la gestion dynamique des devises
 * Permet de récupérer automatiquement la devise d'un pays via l'API REST Countries
 */

import { SUPPORTED_CURRENCIES, DEFAULT_CURRENCY, getCurrencyByCountry } from '../config/currencies';

/**
 * Cache pour éviter les appels répétés à l'API
 */
const currencyCache = new Map();

/**
 * Récupère la devise officielle d'un pays de manière dynamique
 * @param {string} countryCode - Code ISO du pays (ex: 'FR', 'US', 'JP')
 * @returns {Promise<Object>} - Objet contenant le code devise et les informations
 */
export const getCurrencyForCountry = async (countryCode) => {
  if (!countryCode) {
    return {
      code: DEFAULT_CURRENCY,
      symbol: SUPPORTED_CURRENCIES[DEFAULT_CURRENCY]?.symbol || '€',
      name: SUPPORTED_CURRENCIES[DEFAULT_CURRENCY]?.name || 'Euro',
      isSupported: true
    };
  }

  // Vérifier le cache d'abord
  const cacheKey = countryCode.toUpperCase();
  if (currencyCache.has(cacheKey)) {
    return currencyCache.get(cacheKey);
  }

  try {
    // 1. Essayer d'abord avec notre liste supportée
    const supportedCurrency = getCurrencyByCountry(countryCode);
    if (supportedCurrency !== DEFAULT_CURRENCY) {
      const currencyInfo = {
        code: supportedCurrency,
        symbol: SUPPORTED_CURRENCIES[supportedCurrency].symbol,
        name: SUPPORTED_CURRENCIES[supportedCurrency].name,
        isSupported: true
      };
      currencyCache.set(cacheKey, currencyInfo);
      return currencyInfo;
    }

    // 2. Si pas dans notre liste, interroger l'API REST Countries
    console.log(`[CurrencyUtils] Fetching currency for country: ${countryCode}`);
    const response = await fetch(`https://restcountries.com/v3.1/alpha/${countryCode}?fields=currencies`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (data.currencies && Object.keys(data.currencies).length > 0) {
      // Prendre la première devise (généralement il n'y en a qu'une)
      const currencyCode = Object.keys(data.currencies)[0];
      const currencyData = data.currencies[currencyCode];
      
      const currencyInfo = {
        code: currencyCode,
        symbol: currencyData.symbol || currencyCode,
        name: currencyData.name || currencyCode,
        isSupported: false // Pas dans notre liste supportée
      };
      
      console.log(`[CurrencyUtils] Currency found for ${countryCode}:`, currencyInfo);
      currencyCache.set(cacheKey, currencyInfo);
      return currencyInfo;
    }
    
    // Fallback vers devise par défaut
    throw new Error('No currency found for country');
    
  } catch (error) {
    console.warn(`[CurrencyUtils] Error fetching currency for ${countryCode}:`, error);
    
    // Fallback vers devise par défaut
    const fallbackInfo = {
      code: DEFAULT_CURRENCY,
      symbol: SUPPORTED_CURRENCIES[DEFAULT_CURRENCY]?.symbol || '€',
      name: SUPPORTED_CURRENCIES[DEFAULT_CURRENCY]?.name || 'Euro',
      isSupported: true
    };
    
    currencyCache.set(cacheKey, fallbackInfo);
    return fallbackInfo;
  }
};

/**
 * Formate un montant avec la devise appropriée
 * @param {number} amount - Montant à formater
 * @param {Object} currencyInfo - Informations de devise obtenues via getCurrencyForCountry
 * @param {string} locale - Locale pour le formatage (par défaut 'fr-FR')
 * @returns {string} - Montant formaté
 */
export const formatAmountWithCurrency = (amount, currencyInfo, locale = 'fr-FR') => {
  if (!amount || amount === '') return 'N/A';
  
  try {
    // Si la devise est supportée, utiliser notre formatage existant
    if (currencyInfo.isSupported && SUPPORTED_CURRENCIES[currencyInfo.code]) {
      const formatter = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currencyInfo.code,
        minimumFractionDigits: SUPPORTED_CURRENCIES[currencyInfo.code].decimalPlaces,
        maximumFractionDigits: SUPPORTED_CURRENCIES[currencyInfo.code].decimalPlaces
      });
      return formatter.format(Number(amount));
    }
    
    // Sinon, formatage basique avec le symbole
    const formattedAmount = Number(amount).toFixed(2);
    return `${formattedAmount} ${currencyInfo.symbol}`;
    
  } catch (error) {
    console.warn('Error formatting amount:', error);
    return `${Number(amount).toFixed(2)} ${currencyInfo.symbol}`;
  }
};

/**
 * Symboles de devise complète (mappé depuis backend/utils/currency.js)
 */
const FULL_CURRENCY_SYMBOLS = {
  EUR: '€',
  USD: '$',
  GBP: '£',
  JPY: '¥',
  CNY: '¥',
  CHF: 'CHF',
  CAD: '$',
  AUD: '$',
  NZD: '$',
  SEK: 'kr',
  NOK: 'kr',
  DKK: 'kr',
  PLN: 'zł',
  CZK: 'Kč',
  HUF: 'Ft',
  RON: 'lei',
  BGN: 'лв',
  TRY: '₺',
  RUB: '₽',
  INR: '₹',
  THB: '฿',
  SGD: '$',
  HKD: '$',
  MXD: '$',
  MXN: '$',
  BRL: 'R$',
  AED: 'د.إ',
  DZD: 'دج',
  SAR: '﷼'
};

/**
 * Récupère le symbole d'une devise de manière dynamique
 * @param {string} currencyCode - Code de la devise
 * @returns {string} - Symbole de la devise
 */
export const getCurrencySymbol = (currencyCode) => {
  // Vérifier d'abord la liste complète des symboles
  if (FULL_CURRENCY_SYMBOLS[currencyCode]) {
    return FULL_CURRENCY_SYMBOLS[currencyCode];
  }
  
  // Fallback sur notre liste supportée
  if (SUPPORTED_CURRENCIES[currencyCode]) {
    return SUPPORTED_CURRENCIES[currencyCode].symbol;
  }
  
  // Sinon retourner le code comme symbole
  return currencyCode;
};

/**
 * Vide le cache des devises (utile pour les tests ou le rafraîchissement)
 */
export const clearCurrencyCache = () => {
  currencyCache.clear();
  console.log('[CurrencyUtils] Currency cache cleared');
};

/**
 * Récupère les statistiques du cache
 * @returns {Object} - Statistiques du cache
 */
export const getCacheStats = () => {
  return {
    size: currencyCache.size,
    keys: Array.from(currencyCache.keys())
  };
};
